/**
 * Encryption utility for sensitive user data stored in web storage.
 * Uses AES encryption with Web Crypto API for browser environments.
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;

// Fields that should be encrypted in user profile
const ENCRYPTED_FIELDS = ['first_name', 'last_name', 'email', 'tel'];

/**
 * Derive encryption key from a base string using PBKDF2
 * @param {string} baseKey - Base key string
 * @param {Uint8Array} salt - Salt for key derivation
 * @returns {Promise<CryptoKey>}
 */
async function deriveKey(baseKey, salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(baseKey),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Get or generate encryption key for the current session
 * The key is derived from browser-specific data to ensure consistency
 * @returns {Promise<string>}
 */
async function getBaseKey() {
  // Use a combination of factors to create a consistent key per browser
  const fingerprint = [ navigator.hardwareConcurrency, navigator.deviceMemory,navigator.language,Intl.DateTimeFormat().resolvedOptions().timeZone].join('_');

  // Hash the fingerprint to create a consistent base key
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

/**
 * Encrypt a string value
 * @param {string} text - Text to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted data with salt and IV
 */
async function encryptValue(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  try {
    const baseKey = await getBaseKey();
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const key = await deriveKey(baseKey, salt);

    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      data
    );

    // Combine salt + iv + encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);

    // Convert to base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption error:', error);
    return text; // Return original text if encryption fails
  }
}

/**
 * Decrypt a string value
 * @param {string} encryptedText - Base64 encoded encrypted data
 * @returns {Promise<string>} - Decrypted text
 */
async function decryptValue(encryptedText) {
  if (!encryptedText || typeof encryptedText !== 'string') {
    return encryptedText;
  }

  // Check if the value looks like encrypted data (base64)
  const base64Regex = /^[A-Za-z0-9+/=]+$/;
  if (!base64Regex.test(encryptedText)) {
    return encryptedText; // Not encrypted, return as is
  }

  try {
    const baseKey = await getBaseKey();

    // Decode from base64
    const combined = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0));

    // Extract salt, iv, and encrypted data
    const salt = combined.slice(0, SALT_LENGTH);
    const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const data = combined.slice(SALT_LENGTH + IV_LENGTH);

    const key = await deriveKey(baseKey, salt);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv
      },
      key,
      data
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedText; // Return original value if decryption fails
  }
}

/**
 * Encrypt sensitive fields in an object
 * @param {Object} obj - Object containing user data
 * @returns {Promise<Object>} - Object with encrypted sensitive fields
 */
async function encryptSensitiveFields(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const result = { ...obj };

  for (const field of ENCRYPTED_FIELDS) {
    if (result[field]) {
      result[field] = await encryptValue(result[field]);
    }
  }

  return result;
}

/**
 * Decrypt sensitive fields in an object
 * @param {Object} obj - Object with encrypted fields
 * @returns {Promise<Object>} - Object with decrypted sensitive fields
 */
async function decryptSensitiveFields(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const result = { ...obj };

  for (const field of ENCRYPTED_FIELDS) {
    if (result[field]) {
      result[field] = await decryptValue(result[field]);
    }
  }

  return result;
}

/**
 * Check if Web Crypto API is available
 * @returns {boolean}
 */
function isEncryptionSupported() {
  return typeof crypto !== 'undefined' &&
         typeof crypto.subtle !== 'undefined' &&
         typeof crypto.getRandomValues === 'function';
}

module.exports = {
  encryptValue,
  decryptValue,
  encryptSensitiveFields,
  decryptSensitiveFields,
  isEncryptionSupported,
  ENCRYPTED_FIELDS
};
